import { Message } from '../types/chat';

interface ProjectContent {
  id: string;
  name: string;
  readme: string;
  metadata?: {
    technologies?: string[];
    description?: string;
    features?: string[];
  };
}

const skillAreas = [
  'automation testing',
  'Python programming', 
  'API testing',
  'CI/CD pipeline',
  'project leadership',
  'technical expertise',
  'testing frameworks',
  'quality assurance',
  'software development'
];

export const getRandomSkillArea = (): string => {
  return skillAreas[Math.floor(Math.random() * skillAreas.length)];
};

const buildProjectsContext = (projectsContent: ProjectContent[]): string => {
  if (!projectsContent || projectsContent.length === 0) {
    return 'No project details available yet.';
  }

  return projectsContent.map(project => {
    const techStack = project.metadata?.technologies?.join(', ') || 'Various technologies';
    const description = project.metadata?.description || 'No description available';
    
    // Extract key features from README (first few lines or metadata)
    const readmePreview = project.readme 
      ? project.readme.substring(0, 500).replace(/\n+/g, ' ').replace(/#{1,6}\s/g, '').trim()
      : description;

    return `
**${project.name}**
Technologies: ${techStack}
Overview: ${readmePreview}...
${project.metadata?.features ? `Key Features: ${project.metadata.features.slice(0, 3).join(', ')}...` : ''}`;
  }).join('\n');
};

export const buildSystemPrompt = (
  resumeContent: string, 
  projectsContent: ProjectContent[] = [],
  conversationHistory: Message[] = []
): string => {
  const contextSummary = conversationHistory.length > 0
    ? conversationHistory
        .slice(-3)
        .map(m => `${m.role}: ${m.content.substring(0, 100)}${m.content.length > 100 ? '...' : ''}`)
        .join('\n')
    : 'First interaction - be welcoming and helpful';

  const projectsContext = buildProjectsContext(projectsContent);

  return `You are Thanh's Professional AI Assistant - an expert HR consultant specialized in Thanh Nguyen's career profile.

🎯 PRIMARY MISSION:
Help recruiters and HR professionals make informed hiring decisions by providing detailed, accurate information about Thanh's qualifications.

📋 ENHANCED CAPABILITIES:
- Deep analysis of technical skills and experience levels
- Project-specific examples and achievements with detailed technical context
- Career progression insights
- Skill gap analysis for specific roles
- Certification relevance assessment
- Real project implementation details from actual repositories

🚫 STRICT BOUNDARIES:
1. EXCLUSIVELY discuss Thanh's professional background from resume
2. NEVER engage in off-topic conversations
3. Keep responses focused and valuable for hiring decisions
4. Maintain professional tone with personality
5. Optimize for recruiter's time and decision-making

📊 RESUME DATA:
${resumeContent}

🚀 DETAILED PROJECT PORTFOLIO:
${projectsContext}

💡 CONVERSATION CONTEXT:
${contextSummary}

🎭 PERSONALITY GUIDELINES:
- Professional yet approachable
- Confident about Thanh's capabilities  
- Helpful and insightful
- Proactive in highlighting relevant strengths
- Use emojis sparingly but effectively

📝 RESPONSE GUIDELINES:
- Keep responses concise but comprehensive (2-4 paragraphs max)
- Include specific examples from the resume AND detailed project implementations
- Reference actual project architectures, technologies, and features when relevant
- Highlight quantifiable achievements from both resume and project metrics
- Suggest follow-up questions relevant to hiring
- Use bullet points for lists and key information
- Draw connections between resume experience and concrete project examples

🔄 OFF-TOPIC HANDLING:
When encountering off-topic questions, redirect professionally:
"I'm here to help you evaluate Thanh's qualifications! Let me tell you about his ${getRandomSkillArea()} experience instead 🚀"

Remember: Your goal is to help recruiters understand why Thanh is an excellent candidate for technical roles, particularly in automation testing and software quality assurance. Use the detailed project information to provide concrete examples of Thanh's capabilities in action.`;
};

export const buildValidationPrompt = (message: string): string => {
  return `Analyze this message and determine if it's related to professional/career topics or resume evaluation: "${message}"

Respond with only "YES" if it's about:
- Work experience, skills, education
- Technical qualifications or projects
- Career background or achievements  
- Job-related questions
- Professional capabilities

Respond with only "NO" if it's about:
- Personal life, hobbies, opinions
- General conversation or small talk
- Non-professional topics
- Requests for inappropriate content

Response:`;
};

export const sanitizeResumeContent = (content: string): string => {
  // Remove sensitive information and clean up content
  return content
    .replace(/mailto:/gi, '') // Remove mailto links
    .replace(/tel:/gi, '') // Remove tel links  
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert markdown links to plain text
    .replace(/#{1,6}\s/g, '') // Remove markdown headers
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove markdown bold
    .replace(/\*(.+?)\*/g, '$1') // Remove markdown italic
    .replace(/`(.+?)`/g, '$1') // Remove markdown code
    .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines
    .trim();
};

export const estimateResponseTokens = (promptLength: number): number => {
  // Estimate response tokens based on prompt length
  // Typical response is 20-40% of prompt length
  return Math.max(100, Math.min(800, Math.round(promptLength * 0.3)));
};